import { useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { ErrorMessage, Field, Form, Formik } from "formik";
import useProfileUpdate from "../../hooks/useProfileUpdate";
import { HashLoader } from "react-spinners";
import { At, Camera, SpinnerGap } from "@phosphor-icons/react";
import ChangeEmailModal from "../../components/ChangeEmail";
import ChangeAddressModal from "../../components/ChangeAddressModal";
import ChangeUsername from "../../components/ChangeUsername";
import ChanngePhoneNumber from "../../components/ChangePhoneNumberModal";
import { useNavigate, useLocation } from "react-router-dom";
import useGetPhoneOTP from "../../hooks/useGetPhoneOTP";
import { toast } from "sonner";
import { differenceInYears } from "date-fns";
import ReferralInviteCard from "../../components/ReferralInviteCard";
import { leaveSetupTask, readSetupTaskState } from "../../utils/setupTaskReturn";

// Legacy accounts (created before the onboarding flow was tightened) can be
// missing identity details. Normalize a possibly-ISO date to YYYY-MM-DD so it
// renders in a native date input.
const toDateInputValue = (value?: string | null) =>
  value ? String(value).split("T")[0] : "";

const Profile = () => {
  const { user, logout } = useAuth();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const fromSetup = (location.state as { fromSetup?: boolean } | null)?.fromSetup;
  // Forwarded to the phone OTP screen and username modal so a task opened from
  // the setup checklist (or checkout) returns where it came from.
  const setupState = fromSetup ? readSetupTaskState(location.state) : undefined;

  const {
    isGetError: isSendError,
    gettingPhoneOTP: sendingOTP,
    getError: sendError,
    getOTP: sendOTP,
  } = useGetPhoneOTP({
    onSuccess() {
      // Setup state rides along so a check started from checkout returns there.
      navigate("/verify/phone-number?redirect=/home/settings/profile", {
        state: setupState,
      });
    },
  });

  const {
    updateProfile,
    updatingProfile,
    isProfileUpdateError,
    profileUpdateError,
  } = useProfileUpdate({
    onSuccess() {
      toast.success("Profile Updated successfully");
      if (fromSetup) {
        leaveSetupTask(navigate, location.state);
      }
    },
  });

  const [profileImg, setProfileImg] = useState<File | null>(null);
  const imageRef = useRef<HTMLInputElement>(null);

  return (
    <div className="bg-gray-50 px-5 md:px-10 py-10 flex items-center justify-center">
      {showEmailModal && (
        <ChangeEmailModal setShowEmailModal={setShowEmailModal} />
      )}
      {showUsernameModal && (
        <ChangeUsername
          setShowUsernameModal={setShowUsernameModal}
          onUpdated={
            fromSetup
              ? () => leaveSetupTask(navigate, location.state)
              : undefined
          }
        />
      )}
      {showPhoneModal && (
        <ChanngePhoneNumber
          setShowPhoneNumberModal={setShowPhoneModal}
          setupState={setupState}
        />
      )}
      {showAddressModal && (
        <ChangeAddressModal setShowAddressModal={setShowAddressModal} />
      )}
      {updatingProfile && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      <div className="flex flex-col gap-8 max-w-3xl w-full">
        <ReferralInviteCard />
        <div className="flex gap-10 items-center">
          <div
            className="relative cursor-pointer"
            onClick={() => imageRef.current?.click()}
          >
            <img
              src={
                profileImg ? URL.createObjectURL(profileImg) : user?.photo || ""
              }
              alt="you"
              className="w-32 aspect-square object-cover rounded-full bg-white"
            />
            <div className="bg-gray-500/50 text-white absolute bottom-1 left-1/2 -translate-x-1/2 p-2 rounded-full">
              <Camera />
            </div>
          </div>
          <div>
            <div className="font-medium text-deepBlue text-lg">
              Personal Info
            </div>
            <div className="text-gray-700">
              Update your photo and personal details here.
            </div>
            {updatingProfile && (
              <div className="text-amber-500 bg-amber-100 w-fit p-2 text-sm mt-2 rounded-lg px-4 font-medium">
                Updating profile...
              </div>
            )}
            {isProfileUpdateError && (
              <div className="text-red-500 bg-red-100 w-fit p-2 text-sm mt-2 rounded-lg px-4 font-medium">
                {profileUpdateError?.message}
              </div>
            )}
          </div>
        </div>
        <Formik
          enableReinitialize
          initialValues={{
            ...user,
          }}
          onSubmit={(values, { resetForm }) => {
            const data: Record<any, any> = {};

            if (profileImg) {
              data.profile_pics = {
                file: profileImg,
                name: profileImg.name,
              };
            }

            // Only backfill identity fields that were empty on the account.
            // Anything already set (e.g. from BVN/NIN) stays locked and is
            // never resent here.
            const addIfNewlyProvided = (
              key: string,
              original: unknown,
              next: unknown
            ) => {
              const trimmed = typeof next === "string" ? next.trim() : next;
              if (!original && trimmed) {
                data[key] = trimmed;
              }
            };

            addIfNewlyProvided("firstname", user?.firstname, values.firstname);
            addIfNewlyProvided("lastname", user?.lastname, values.lastname);
            addIfNewlyProvided(
              "middlename",
              user?.middlename,
              values.middlename
            );
            addIfNewlyProvided("gender", user?.gender, values.gender);
            addIfNewlyProvided(
              "place_of_birth",
              user?.place_of_birth,
              values.place_of_birth
            );

            // Date of birth: only when missing, formatted YYYY-MM-DD, 18+ only.
            if (!user?.date_of_birth && values.date_of_birth) {
              const dob = String(values.date_of_birth).split("T")[0];
              if (differenceInYears(new Date(), new Date(dob)) < 18) {
                toast.error("You must be at least 18 years old.");
                return;
              }
              data.date_of_birth = dob;
            }

            updateProfile(
              {
                ...data,
                address: values.address,
              },
              {
                onSuccess() {
                  resetForm();
                },
              }
            );
          }}
        >
          {({ dirty, values, handleChange, handleBlur }) => {
            return (
              <Form className="md:bg-white rounded-lg w-full shadow-sm flex-col gap-5 flex">
                <div className="md:p-10 py-10 w-full flex-col gap-5 flex">
                  <input
                    type="file"
                    hidden
                    ref={imageRef}
                    name="profile_img"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setProfileImg(e.target.files[0]);
                      }
                    }}
                    id="profile_img"
                    accept="image/png, image/jpeg, image/jpg"
                  />
                  <div className="flex gap-5 flex-col md:flex-row">
                    <div className="flex flex-col gap-2 w-full flex-1">
                      <label
                        htmlFor="firstname"
                        className="text-sm font-bold text-gray-700"
                      >
                        First Name
                      </label>
                      <Field
                        disabled={!!user?.firstname}
                        type="text"
                        id="firstname"
                        name="firstname"
                        placeholder="Enter your First Name"
                        className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full disabled:bg-gray-50"
                      />
                      <ErrorMessage
                        name="firstname"
                        component="div"
                        className="text-red-500"
                      />
                    </div>
                    <div className="flex flex-col gap-2 w-full flex-1">
                      <label
                        htmlFor="phone"
                        className="text-sm font-bold text-gray-700"
                      >
                        Last Name
                      </label>
                      <Field
                        type="text"
                        disabled={!!user?.lastname}
                        id="lastname"
                        name="lastname"
                        placeholder="Enter your Last Name"
                        className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full disabled:bg-gray-50"
                      />
                      <ErrorMessage
                        name="lastname"
                        component="div"
                        className="text-red-500"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 w-full ">
                    <label
                      htmlFor="middlename"
                      className="text-sm font-bold text-gray-700"
                    >
                      Middlename
                    </label>
                    <Field
                      disabled={!!user?.middlename}
                      type="text"
                      id="middlename"
                      name="middlename"
                      placeholder="Middlename"
                      className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full disabled:bg-gray-50"
                    />
                    <ErrorMessage
                      name="middlename"
                      component="div"
                      className="text-red-500"
                    />
                  </div>
                  <div className="flex gap-5 flex-col md:flex-row">
                    <div className="flex flex-col gap-2 w-full flex-1">
                      <label
                        htmlFor="date_of_birth"
                        className="text-sm font-bold text-gray-700"
                      >
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        id="date_of_birth"
                        name="date_of_birth"
                        disabled={!!user?.date_of_birth}
                        value={toDateInputValue(values.date_of_birth)}
                        onChange={handleChange("date_of_birth")}
                        onBlur={handleBlur("date_of_birth")}
                        max={new Date().toISOString().split("T")[0]}
                        className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full disabled:bg-gray-50"
                      />
                    </div>
                    <div className="flex flex-col gap-2 w-full flex-1">
                      <label
                        htmlFor="gender"
                        className="text-sm font-bold text-gray-700"
                      >
                        Gender
                      </label>
                      <Field
                        as="select"
                        id="gender"
                        name="gender"
                        disabled={!!user?.gender}
                        className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full bg-white disabled:bg-gray-50"
                      >
                        <option value="">Select your gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </Field>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 w-full ">
                    <label
                      htmlFor="place_of_birth"
                      className="text-sm font-bold text-gray-700"
                    >
                      Place of Birth
                    </label>
                    <Field
                      type="text"
                      id="place_of_birth"
                      name="place_of_birth"
                      disabled={!!user?.place_of_birth}
                      placeholder="Enter your place of birth"
                      className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full disabled:bg-gray-50"
                    />
                  </div>
                  <div className="flex flex-col gap-2 w-full ">
                    <label
                      htmlFor="nin"
                      className="text-sm font-bold text-gray-700"
                    >
                      NIN (National Identity Number)
                    </label>
                    <div
                      id="nin"
                      className="rounded-lg border border-gray-300 shadow px-4 w-full flex justify-between items-center py-3"
                    >
                      <div>{values.compliance?.nin || "No NIN"}</div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 w-full ">
                    <label
                      htmlFor="email"
                      className="text-sm font-bold text-gray-700"
                    >
                      Email
                    </label>
                    <div
                      id="email"
                      className="rounded-lg border border-gray-300 shadow px-4 py-2 w-full flex justify-between items-center"
                    >
                      <div>{values.email}</div>
                      <button
                        type="button"
                        onClick={() => setShowEmailModal(true)}
                        className="text-deepBlue font-medium text-sm p-2 bg-deepBlue/10 transition-all rounded-md"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 w-full ">
                    <label
                      htmlFor="username"
                      className="text-sm font-bold text-gray-700"
                    >
                      Username
                    </label>
                    <div
                      id="username"
                      className="rounded-lg border border-gray-300 shadow px-4 py-2 gap-2 w-full flex justify-between items-center"
                    >
                      <At className="text-gray-500 size-4" weight="bold" />
                      <div className="flex-1">{values.username}</div>
                      <button
                        onClick={() => setShowUsernameModal(true)}
                        type="button"
                        className="text-deepBlue font-medium text-sm p-2 bg-deepBlue/10 transition-all rounded-md"
                      >
                        {values.username ? "Change" : "Create"}
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-2 w-full ">
                      <label
                        htmlFor="phone_number"
                        className="text-sm font-bold text-gray-700"
                      >
                        Phone Number
                      </label>
                      <div
                        id="phone_number"
                        className="rounded-lg border border-gray-300 shadow px-4 py-2 gap-2 w-full flex justify-between items-center"
                      >
                        <div className="flex-1">
                          {values.phone_number?.replace(/^\+?234/, "") ||
                            "No Phone Number Added"}
                        </div>
                        {values.phone_number &&
                          !user?.phone_verified === true && (
                            <button
                              onClick={() => {
                                sendOTP({});
                              }}
                              type="button"
                              className="text-deepBlue font-medium text-sm p-2 bg-deepBlue/10 transition-all rounded-md"
                            >
                              {sendingOTP ? (
                                <div>
                                  <SpinnerGap
                                    className="animate-spin"
                                    weight="bold"
                                  />
                                </div>
                              ) : (
                                <div>Verify</div>
                              )}
                            </button>
                          )}
                        <button
                          onClick={() => setShowPhoneModal(true)}
                          type="button"
                          className="text-deepBlue font-medium text-sm p-2 bg-deepBlue/10 transition-all rounded-md"
                        >
                          {values.phone_number ? "Change" : "Add"}
                        </button>
                      </div>
                    </div>
                    {isSendError && (
                      <div className="text-red-500 text-center flex w-full">
                        {sendError?.message}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full ">
                    <label
                      htmlFor="address"
                      className="text-sm font-bold text-gray-700"
                    >
                      Home Address
                    </label>
                    <div
                      id="address"
                      className="rounded-lg border border-gray-300 shadow px-4 gap-2 w-full flex justify-between items-center focus-within:outline"
                    >
                      <Field
                        type="text"
                        id="address"
                        name="address"
                        placeholder="No address"
                        className="py-3 w-full focus:outline-none bg-transparent"
                      />
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-100 flex items-center justify-between py-5 lg:px-10">
                  <button
                    onClick={logout}
                    type="button"
                    className="p-4 px-5 border-red-100 border rounded-lg font-bold bg-red-100 text-red-500 text-sm"
                  >
                    Log Out
                  </button>
                  <button
                    disabled={!(profileImg != null || dirty)}
                    type="submit"
                    className="p-4 px-5 border-visaro-gray-700 border rounded-lg font-bold bg-deepBlue text-white text-sm"
                  >
                    Save Changes
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default Profile;
